import React, { useState, useEffect } from 'react';
import { RiCloseLine, RiUploadLine } from 'react-icons/ri';
import Button from '@/components/common/Button/Button';
import Input from '@/components/forms/Input/Input';
import Spinner from '@/components/common/Spinner/Spinner';
import { type AdminCategory } from '@/hooks/admin/useAdmin';
import { useCreateCategory, useUpdateCategory } from '@/hooks/admin/useAdminCategories';

interface CategoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    category?: AdminCategory;
    onSuccess?: () => void;
}

const slugify = (text: string): string => {
    return text
        .toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')           // Replace spaces with hyphens
        .replace(/[^\w\-]+/g, '')       // Remove special characters
        .replace(/\-\-+/g, '-')         // Replace multiple hyphens with one
        .replace(/^-+/, '')             // Remove hyphens from start
        .replace(/-+$/, '');            // Remove hyphens from end
};

const CategoryModal: React.FC<CategoryModalProps> = ({
    isOpen,
    onClose,
    category,
    onSuccess
}) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        image_url: '',
        is_active: true,
        sort_order: 0
    });
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string>('');

    const createCategory = useCreateCategory();
    const updateCategory = useUpdateCategory();

    const isEditing = !!category;
    const isLoading = createCategory.isPending || updateCategory.isPending;

    useEffect(() => {
        if (category) {
            setFormData({
                name: category.name,
                description: category.description || '',
                image_url: category.image_url || '',
                is_active: category.is_active,
                sort_order: category.sort_order
            });
            setImagePreview(category.image_url || '');
        } else {
            setFormData({
                name: '',
                description: '',
                image_url: '',
                is_active: true,
                sort_order: 0
            });
            setImagePreview('');
        }
        setImageFile(null);
    }, [category, isOpen]);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onload = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            alert('El nombre de la categoría es requerido');
            return;
        }

        try {
            // TODO: Implement file upload to get image URL
            const imageUrl = imageFile ? 'uploaded-image-url' : formData.image_url;

            const categoryData = {
                ...formData,
                image_url: imageUrl,
                slug: slugify(formData.name)
            };

            if (isEditing && category) {
                await updateCategory.mutateAsync({
                    id: category.id,
                    data: categoryData
                });
            } else {
                await createCategory.mutateAsync(categoryData);
            }

            onSuccess?.();
            onClose();
        } catch (error) {
            console.error('Error saving category:', error);
            alert('Error al guardar la categoría');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="admin-modal-overlay">
            <div className="admin-modal-content">
                <div className="admin-modal-header">
                    <h2 className="admin-modal-title">
                        {isEditing ? 'Editar Categoría' : 'Nueva Categoría'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="admin-modal-close"
                        disabled={isLoading}
                    >
                        <RiCloseLine />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="admin-modal-form">
                    <div className="admin-form-grid">
                        <div className="admin-form-group">
                            <Input
                                label="Nombre"
                                name="name"
                                value={formData.name}
                                setValue={(value) => setFormData(prev => ({ ...prev, name: value }))}
                                placeholder="Ingrese el nombre de la categoría"
                                required
                            />
                        </div>

                        <div className="admin-form-group">
                            <label className="admin-form-label">Imagen de la Categoría</label>
                            <div className="admin-image-upload">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="admin-file-input"
                                    id="category-image"
                                />
                                <label htmlFor="category-image" className="admin-file-label">
                                    <RiUploadLine className="admin-upload-icon" />
                                    <span>Seleccionar Imagen</span>
                                </label>
                                {imagePreview && (
                                    <div className="admin-image-preview">
                                        <img src={imagePreview} alt="Preview" />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setImagePreview('');
                                                setImageFile(null);
                                                setFormData(prev => ({ ...prev, image_url: '' }));
                                            }}
                                            className="admin-image-remove"
                                        >
                                            <RiCloseLine />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="admin-form-group admin-form-full">
                            <Input
                                label="Descripción"
                                name="description"
                                value={formData.description}
                                setValue={(value) => setFormData(prev => ({ ...prev, description: value }))}
                                placeholder="Descripción de la categoría (opcional)"
                                rows={3}
                            />
                        </div>

                        <div className="admin-form-group">
                            <Input
                                label="Orden de Visualización"
                                name="sort_order"
                                type="number"
                                value={formData.sort_order.toString()}
                                setValue={(value) => setFormData(prev => ({ ...prev, sort_order: parseInt(value) || 0 }))}
                                placeholder="0"
                            />
                        </div>

                        <div className="admin-form-group">
                            <label className="admin-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                                />
                                <span>Categoría Activa</span>
                            </label>
                        </div>
                    </div>

                    <div className="admin-modal-actions">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={isLoading}
                        >
                            {isLoading ? <Spinner size="sm" /> : null}
                            {isEditing ? 'Actualizar' : 'Crear'} Categoría
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CategoryModal;
